import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import type { DialogueLine, ScriptRefinementResponse, ScriptSuggestionChange } from '../../../types/script-refinement'
import { buildScriptSuggestionChanges } from '../utils/script-suggestions'

const STORAGE_PREFIX = 'script-suggestions:'
const STORAGE_TTL_MS = 12 * 60 * 60 * 1000 // 12 hours

type SuggestionMetadata = {
  message: string
  summary?: string | null
  diffPreview?: string | null
  linesChanged?: number | null
  tokenUsage?: number | null
  generatedAt: string
  instruction: string
}

type ScriptSuggestionsState = {
  podcastId: string | null
  metadata: SuggestionMetadata | null
  originalDialogue: DialogueLine[]
  proposedDialogue: DialogueLine[]
  changes: ScriptSuggestionChange[]
  activeChangeId: string | null
}

type ScriptSuggestionsActions = {
  hydrate: (podcastId: string) => void
  setSuggestions: (podcastId: string, payload: ScriptRefinementResponse) => void
  clear: (podcastId?: string) => void
  markAccepted: (changeId: string) => void
  markRejected: (changeId: string) => void
  setActive: (changeId: string | null) => void
  jump: (direction: 'next' | 'prev') => void
}

type StoreState = ScriptSuggestionsState & ScriptSuggestionsActions

type PersistedSuggestions = {
  savedAt: number
  state: ScriptSuggestionsState
}

const snapshotState = (state: StoreState): ScriptSuggestionsState => ({
  podcastId: state.podcastId,
  metadata: state.metadata,
  originalDialogue: state.originalDialogue,
  proposedDialogue: state.proposedDialogue,
  changes: state.changes,
  activeChangeId: state.activeChangeId,
})

const readFromStorage = (podcastId: string): ScriptSuggestionsState | null => {
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${podcastId}`)
    if (!raw) return null
    const parsed = JSON.parse(raw) as PersistedSuggestions
    if (!parsed?.savedAt) return null
    if (Date.now() - parsed.savedAt > STORAGE_TTL_MS) {
      window.localStorage.removeItem(`${STORAGE_PREFIX}${podcastId}`)
      return null
    }
    return parsed.state
  } catch {
    return null
  }
}

const writeToStorage = (podcastId: string, state: ScriptSuggestionsState) => {
  try {
    const payload: PersistedSuggestions = { savedAt: Date.now(), state }
    window.localStorage.setItem(`${STORAGE_PREFIX}${podcastId}`, JSON.stringify(payload))
  } catch {
    // ignore
  }
}

const removeFromStorage = (podcastId: string) => {
  try {
    window.localStorage.removeItem(`${STORAGE_PREFIX}${podcastId}`)
  } catch {
    // ignore
  }
}

const clearBackendSuggestionsCache = async (podcastId: string) => {
  try {
    const { apiRequest } = await import('../../../api/config')
    await apiRequest(`/api/podcast/${podcastId}/refine-suggestions`, { 
      method: 'DELETE' 
    })
  } catch (error) {
    // Silently ignore errors - this is a best-effort cleanup
    console.debug('Failed to clear backend suggestions cache', error)
  }
}

const initialState: ScriptSuggestionsState = {
  podcastId: null,
  metadata: null,
  originalDialogue: [],
  proposedDialogue: [],
  changes: [],
  activeChangeId: null,
}

export const useScriptSuggestionsStore = create<StoreState>()(immer((set, get) => ({
  ...initialState,
  hydrate: (podcastId) => {
    if (typeof window === 'undefined') return
    const existing = readFromStorage(podcastId)
    if (!existing) {
      set((state) => {
        if (state.podcastId === podcastId) {
          state.metadata = null
          state.originalDialogue = []
          state.proposedDialogue = []
          state.changes = []
          state.activeChangeId = null
        }
      })
      return
    }
    set(() => ({ ...existing, podcastId }))
  },
  setSuggestions: (podcastId, payload) => {
    const changes = buildScriptSuggestionChanges(payload.originalDialogue, payload.proposedDialogue)
    const metadata: SuggestionMetadata = {
      message: payload.message,
      summary: payload.summary,
      diffPreview: payload.diffPreview,
      linesChanged: payload.linesChanged ?? null,
      tokenUsage: payload.tokenUsage ?? null,
      generatedAt: payload.generatedAt,
      instruction: payload.instruction,
    }
    const nextState: ScriptSuggestionsState = {
      podcastId,
      metadata,
      originalDialogue: payload.originalDialogue,
      proposedDialogue: payload.proposedDialogue,
      changes,
      activeChangeId: changes[0]?.id ?? null,
    }
    set(() => nextState)
    if (typeof window !== 'undefined') {
      writeToStorage(podcastId, nextState)
    }
  },
  clear: (podcastId) => {
    const currentPodcastId = get().podcastId
    const target = podcastId ?? currentPodcastId
    set(() => ({ ...initialState, podcastId: target ?? null }))
    if (typeof window !== 'undefined' && target) {
      removeFromStorage(target)
      // Clear the cached progress event on the backend
      void clearBackendSuggestionsCache(target)
    }
  },
  markAccepted: (changeId) => {
    set((state) => {
      const idx = state.changes.findIndex((c) => c.id === changeId)
      if (idx === -1) return
      state.changes.splice(idx, 1)
      if (state.activeChangeId === changeId) {
        state.activeChangeId = state.changes[idx]?.id ?? state.changes[idx - 1]?.id ?? state.changes[0]?.id ?? null
      }
      if (state.changes.length === 0) {
        state.metadata = null
        state.originalDialogue = []
        state.proposedDialogue = []
        state.activeChangeId = null
      }
    })
    const { podcastId } = get()
    if (podcastId && typeof window !== 'undefined') {
      const snapshot = snapshotState(get())
      if (snapshot.changes.length === 0) {
        removeFromStorage(podcastId)
      } else {
        writeToStorage(podcastId, snapshot)
      }
    }
  },
  markRejected: (changeId) => {
    set((state) => {
      const idx = state.changes.findIndex((c) => c.id === changeId)
      if (idx === -1) return
      state.changes.splice(idx, 1)
      if (state.activeChangeId === changeId) {
        state.activeChangeId = state.changes[idx]?.id ?? state.changes[idx - 1]?.id ?? state.changes[0]?.id ?? null
      }
      if (state.changes.length === 0) {
        state.metadata = null
        state.originalDialogue = []
        state.proposedDialogue = []
        state.activeChangeId = null
      }
    })
    const { podcastId } = get()
    if (podcastId && typeof window !== 'undefined') {
      const snapshot = snapshotState(get())
      if (snapshot.changes.length === 0) {
        removeFromStorage(podcastId)
      } else {
        writeToStorage(podcastId, snapshot)
      }
    }
  },
  setActive: (changeId) => {
    if (changeId === null) {
      set((state) => { state.activeChangeId = null })
      return
    }
    const exists = get().changes.some((c) => c.id === changeId)
    if (!exists) return
    set((state) => { state.activeChangeId = changeId })
  },
  jump: (direction) => {
    const { changes, activeChangeId } = get()
    if (changes.length === 0) return
    if (!activeChangeId) {
      set((state) => { state.activeChangeId = changes[0].id })
      return
    }
    const idx = changes.findIndex((c) => c.id === activeChangeId)
    if (idx === -1) {
      set((state) => { state.activeChangeId = changes[0].id })
      return
    }
    const nextIdx = direction === 'next'
      ? Math.min(idx + 1, changes.length - 1)
      : Math.max(idx - 1, 0)
    set((state) => { state.activeChangeId = changes[nextIdx].id })
  },
})))
